import { ClientAccountWithAnalyticSummary } from '@lune-climate/lune/esm/models/ClientAccountWithAnalyticSummary'
import { MainLayoutContainerNew, Pagination, SearchInput } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'

import WhiteBoxContainer from 'components/WhiteBoxContainer'
import useClientAccounts from 'hooks/useClientAccounts'
import useIsTestMode from 'hooks/useIsTestMode'
import useTitle from 'hooks/useTitle'
import { queryKeys } from 'queryKeys'
import { orderByDateDesc } from 'utils'
import {
    ClientAccountsTable,
    EmptyClientAccounts,
    NewClientAccountButton,
} from 'views/Settings/ClientAccounts'

const ClientAccounts = () => {
    useTitle('Client Accounts')
    const isTestMode = useIsTestMode()
    const ITEMS_PER_PAGE = 50
    const queryClient = useQueryClient()

    const [searchKey, setSearchKey] = useState<string | undefined>(undefined)
    const pageHistoryRef = useRef<(string | undefined)[]>([undefined])
    const [currentPage, setCurrentPage] = useState(1)

    // pageHistoryRef is 0-indexed for page 1, 1-indexed for page 2, etc.
    const cursor = pageHistoryRef.current[currentPage - 1]

    const { paginatedAccounts, loading, hasNextPage } = useClientAccounts(
        searchKey,
        cursor,
        true,
        ITEMS_PER_PAGE,
    )

    useEffect(() => {
        if (paginatedAccounts?.hasMore && paginatedAccounts.nextCursor) {
            const nextCursor = paginatedAccounts.nextCursor
            const newHistory = [...pageHistoryRef.current]
            newHistory[currentPage] = nextCursor
            pageHistoryRef.current = newHistory
        }
    }, [paginatedAccounts, currentPage])

    useEffect(() => {
        setCurrentPage(1)
        pageHistoryRef.current = [undefined]
    }, [searchKey])

    const handleOnSearch = (searchKey: string) => {
        if (searchKey.trim() === '') {
            setSearchKey(undefined)
        } else {
            setSearchKey(searchKey)
        }
    }

    const handleOnClear = () => {
        setSearchKey(undefined)
    }

    const clientAccounts: ClientAccountWithAnalyticSummary[] = (paginatedAccounts?.data ?? []).sort(
        orderByDateDesc((account) => account.lastActiveDate),
    )

    const handlePageChange = (page: number) => {
        setCurrentPage(page)
    }

    const noClientAccountsCreated = !!paginatedAccounts && clientAccounts.length === 0 && !searchKey

    const onAccountCreation = async () => {
        // Reset pagination to page 1 first
        setCurrentPage(1)
        pageHistoryRef.current = [undefined]

        // Invalidate queries - this will trigger a refetch with the new cursor (undefined for page 1)
        await queryClient.invalidateQueries(queryKeys.GET_CLIENT_ACCOUNTS)
    }

    return noClientAccountsCreated ? (
        <EmptyClientAccounts onAccountCreation={() => onAccountCreation()} />
    ) : (
        <MainLayoutContainerNew
            isTestMode={!!isTestMode}
            title={'Client accounts'}
            subtitle={'View and manage your customers’ client accounts'}
            sx={{
                height: isTestMode ? `calc(100vh - 24px)` : '100vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '.main-layout-container__header': {
                    top: 0,
                },
                '.main-layout-container__body': {
                    overflowY: 'hidden',
                },
            }}
            primaryButton={
                <NewClientAccountButton
                    onModalClose={(accountCreated) => {
                        if (accountCreated) {
                            onAccountCreation()
                        }
                    }}
                />
            }
        >
            <WhiteBoxContainer
                sx={{
                    overflow: 'hidden',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box>
                        <SearchInput onSearch={handleOnSearch} onClear={handleOnClear} />
                    </Box>
                    {clientAccounts.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            itemsPerPage={ITEMS_PER_PAGE}
                            currentPageItemCount={clientAccounts.length}
                            onPageChange={handlePageChange}
                            defaultHasNext={!!hasNextPage}
                            loading={loading}
                        />
                    )}
                </Box>
                <ClientAccountsTable
                    accounts={clientAccounts}
                    loading={loading}
                    currentPage={currentPage}
                    pageHistoryRef={pageHistoryRef}
                />
            </WhiteBoxContainer>
        </MainLayoutContainerNew>
    )
}

export default ClientAccounts
